import { useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import './OrderDocuments.css'
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import { DeleteOutline, DownloadOutlined, UploadFile } from '@mui/icons-material'
import { useRepCodes } from 'src/hooks'
import { confidenceType, fileInputType, portalOrderType } from './orderTypes'
import HeaderCRMDetails from '../../lines/HeaderCRMDetails'
import { useQuoteLines } from '../../lines/QuoteLinesContext'
import { getQuoteCrm } from '../../quoteCrmCache'
import { downloadQuotePdf } from '../export/QuotePdfDocument'
import {
  AddressDetails,
  EMPTY_ADDRESS,
  aeFromRepCode,
  isrFromRepCode,
  shipToFromCrm,
  soldToFromCrm,
} from '../export/quotePdfModel'
import { dpasRatingOptions } from 'src/lists'

function OrderDocuments({
  quoteId,
  order,
  files,
  orderConf,
  confChanges,
  setView,
  setFiles,
  setOrder,
  skipAi,
  setSkipAi,
}: {
  quoteId: string
  order: portalOrderType
  files: fileInputType
  orderConf: React.MutableRefObject<confidenceType>
  confChanges: React.MutableRefObject<confidenceType>
  setOrder: React.Dispatch<React.SetStateAction<portalOrderType>>
  setFiles: React.Dispatch<React.SetStateAction<fileInputType>>
  setView: React.Dispatch<React.SetStateAction<number>>
  skipAi: boolean
  setSkipAi: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { quote, lineItems, vendorQuotes } = useQuoteLines()
  const [repCodes] = useRepCodes()
  const rep = repCodes.find(r => r.RepCode === quote.RepCode) ?? null
  const isrDetails = isrFromRepCode(rep)
  const aeDetails = aeFromRepCode(rep)
  const supplierQuotes = vendorQuotes.filter(v => v.Source !== 'ImpresCatalog')

  const [riskBuyCompliant, setRiskBuyCompliant] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState<string | null>(null)
  const [aiState, setAiState] = useState<'loading' | 'error' | ''>('')
  const [soldToDetails, setSoldToDetails] = useState<AddressDetails>({ ...EMPTY_ADDRESS })
  const [shippingDetails, setShippingDetails] = useState<AddressDetails>({ ...EMPTY_ADDRESS })

  const govAiResult = useRef<
    Partial<{
      CustomerPO: string
      GovPaymentTerms: string
      DeliveryDueDate: Date
      AwardDate: Date
      OrderTotal: number
    }>
  >({})

  useEffect(() => {
    if (!quoteId) return
    getQuoteCrm(quoteId)
      .then(({ account, contact }) => {
        setSoldToDetails(soldToFromCrm(account, contact))
        setShippingDetails(shipToFromCrm(account, contact))
      })
      .catch(err => console.error(err))
  }, [quoteId])

  const DocumentUpload = useMemo(
    () =>
      function DocumentUpload({
        label,
        fileKey,
        required = false,
        multiple = false,
        children,
      }: {
        label: string
        fileKey: keyof fileInputType
        required?: boolean
        multiple?: boolean
        children?: React.ReactNode
      }) {
        return (
          <Grid container direction="column" spacing={2} className="order-documents-item">
            <Grid>
              <Typography variant="body2" color="text.secondary">
                {label} {required ? '(Required)' : ''}
              </Typography>
            </Grid>
            <Grid container spacing={2} alignItems="center">
              <Grid>
                <Button
                  variant="outlined"
                  component="label"
                  size="large"
                  startIcon={<UploadFile />}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop(fileKey, multiple)}
                >
                  Choose File
                  <input
                    type="file"
                    multiple={multiple}
                    onChange={e => {
                      if (fileKey === 'governmentaward') setSkipAi(false)
                      setFiles(prev => ({
                        ...prev,
                        [fileKey]: [...(multiple ? prev[fileKey] : []), ...Array.from(e.target.files ?? [])],
                      }))
                    }}
                  />
                </Button>
              </Grid>
              <Grid>
                <Button
                  disabled={files[fileKey].length === 0}
                  onClick={() => {
                    setFiles(prev => ({ ...prev, [fileKey]: [] }))
                  }}
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
            <Grid>
              <List dense className="order-documents-list">
                {files[fileKey].map(file => (
                  <ListItem disablePadding key={file.name}>
                    <ListItemText
                      primary={
                        <Grid container alignItems="center" className="order-documents-list-item-text">
                          <Grid size="grow">{file.name}</Grid>
                          <Grid>
                            <IconButton
                              onClick={() =>
                                setFiles(prev => ({
                                  ...prev,
                                  [fileKey]: prev[fileKey].filter(f => f.name !== file.name),
                                }))
                              }
                            >
                              <DeleteOutline />
                            </IconButton>
                          </Grid>
                        </Grid>
                      }
                    />
                  </ListItem>
                ))}
              </List>
              {children}
            </Grid>
          </Grid>
        )
      },
    [files, setFiles, setSkipAi],
  )

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = (key: keyof fileInputType, multiple: boolean) => (e: React.DragEvent) => {
    e.preventDefault()
    if (key === 'governmentaward') setSkipAi(false)
    const dropped = Array.from(e.dataTransfer.files)
    setFiles(prev => ({ ...prev, [key]: multiple ? [...prev[key], ...dropped] : dropped.slice(0, 1) }) as fileInputType)
  }

  const applyAiResultsAndAdvance = () => {
    setOrder(prev => {
      const next = { ...prev, ...govAiResult.current }
      return {
        ...next,
        GrossMargin: order.PurchaseVehicle === 'ICPT UCOR' ? 0 : order.GrossMargin,
      }
    })

    confChanges.current = { ...confChanges.current, ...orderConf.current }
    setAiState('')
    setView(1)
  }

  const handleReadAi = () => {
    if (skipAi) {
      setView(1)
      return
    }

    setAiState('loading')
    govAiResult.current = {}

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const aiPromises: Promise<any>[] = []
    for (const file of files.governmentaward) {
      if (!file.name.endsWith('.pdf')) continue
      const AIfile = new FormData()
      AIfile.append('file', file, 'govAward_' + file.name)
      aiPromises.push(
        axios
          .post(`/quote/${quoteId}/orderAI`, AIfile)
          .then(res => {
            if (res.data !== 'Not a PDF') {
              govAiResult.current = res.data.order
              orderConf.current = { ...orderConf.current, ...res.data.confidence }
            }
          })
          .catch(err => {
            if (err.response) {
              console.error(err.response)
            } else if (err.request) {
              console.error(err.request)
            } else {
              console.error(err.message)
            }
            throw err
          }),
      )
    }
    Promise.all(aiPromises)
      .then(() => {
        setSkipAi(true)
        applyAiResultsAndAdvance()
      })
      .catch(() => setAiState('error'))
  }

  useEffect(() => {
    setOrder(prev => ({
      ...prev,
      MultipleVendorQuotes: files.vendorquote.length > 1 || supplierQuotes.length > 1,
    }))
  }, [files.vendorquote.length, supplierQuotes.length, setOrder])

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={6} container spacing={1} className="order-documents-container">
          <Grid size={12}>
            <Typography variant="h6">Order Flags</Typography>
          </Grid>
          <Grid size={12} container spacing={1} alignItems="center" justifyContent="space-between">
            <FormControlLabel
              control={<Checkbox checked={!!order.Urgent} />}
              label="This order is URGENT"
              onChange={(_e, checked) => {
                setOrder(prev => ({ ...prev, Urgent: checked }))
                if (!checked) {
                  setOrder(prev => ({ ...prev, UrgentReason: null }))
                }
              }}
            />
            <TextField
              select
              label="Reason"
              size="small"
              disabled={!order.Urgent}
              value={order.UrgentReason ?? ''}
              onChange={e => {
                setOrder(prev => ({ ...prev, UrgentReason: e.target.value }))
              }}
            >
              <MenuItem value="PoP">PoP</MenuItem>
              <MenuItem value="Cost Implications">Cost Implications</MenuItem>
            </TextField>
          </Grid>
          <Grid size={12} container spacing={1} alignItems="center" justifyContent="space-between">
            <Typography>DPAS Rating</Typography>
            <TextField
              select
              label="DPAS Rating"
              size="small"
              value={order.DPASRating ?? ''}
              onChange={e => setOrder(prev => ({ ...prev, DPASRating: e.target.value }))}
            >
              {dpasRatingOptions.map(rating => (
                <MenuItem key={rating} value={rating}>
                  {rating}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={12} container spacing={1} justifyContent="space-between" alignItems="center">
            <FormControlLabel
              control={<Checkbox checked={!!order.RiskBuy} />}
              label="Risk Buy"
              onChange={(_e, checked) => {
                setOrder(prev => ({ ...prev, RiskBuy: checked }))
                !checked && setRiskBuyCompliant(false)
              }}
            />
            <FormControlLabel
              control={<Checkbox checked={riskBuyCompliant} />}
              label="This order has been confirmed to be compliant and/or approved"
              disabled={!order.RiskBuy}
              onChange={(_e, checked) => setRiskBuyCompliant(checked)}
            />
          </Grid>
        </Grid>
        <Grid size={6} container spacing={1} className="order-documents-files-container">
          <Grid size={12}>
            <Typography variant="h6">Order Header Details</Typography>
          </Grid>
          <Grid size={12} container spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Contract Number:{' '}
            </Typography>
            <Typography>{order.ContractNumber ?? ''}</Typography>
          </Grid>
          <Grid size={12} container spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Rep Code:{' '}
            </Typography>
            <Typography>{order.RepCode ?? ''}</Typography>
          </Grid>
          <Grid size={12} container spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Opportunity:{' '}
            </Typography>
            <Typography>{order.Opportunity ?? ''}</Typography>
          </Grid>
          <Grid size={12}>
            <Button variant="outlined" onClick={() => setDrawerOpen('opportunity')}>
              View CRM Details
            </Button>
          </Grid>
        </Grid>
        <Grid size={12} container spacing={1} className="order-documents-files-container">
          <Grid size={12}>
            <Typography variant="h6">Document Upload</Typography>
          </Grid>
          <Grid size={12} container spacing="24px">
            <Grid size={3}>
              <Grid container direction="column" spacing={2} className="order-documents-item">
                <Grid>
                  <Typography variant="body2" color="text.secondary">
                    IMPRES Quote
                  </Typography>
                </Grid>
                <Grid>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadOutlined />}
                    onClick={() =>
                      downloadQuotePdf({
                        quote,
                        lineItems,
                        soldToDetails,
                        shippingDetails,
                        isrDetails,
                        aeDetails,
                      }).catch(err => console.error(err))
                    }
                  >
                    Download Quote
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            <Grid size={3}>
              <DocumentUpload label="Government Award" fileKey="governmentaward" required />
            </Grid>
            <Grid size={3}>
              <DocumentUpload
                label="Supplier Quote(s)"
                fileKey="vendorquote"
                multiple
                required={order.PurchaseVehicle !== 'ICPT UCOR' && supplierQuotes.length === 0}
              >
                <List dense className="order-documents-list">
                  {supplierQuotes.length === 0 ? (
                    <ListItem disablePadding>
                      <ListItemText primary="No supplier quotes found" />
                    </ListItem>
                  ) : (
                    supplierQuotes.map(vendorQuote => (
                      <ListItem disablePadding key={vendorQuote.VendorQuoteId}>
                        <ListItemText
                          primary={
                            [vendorQuote.VendorName, vendorQuote.VendorQuoteNumber].filter(Boolean).join(': ') ||
                            'Supplier Quote'
                          }
                        />
                      </ListItem>
                    ))
                  )}
                </List>
              </DocumentUpload>
            </Grid>
            <Grid size={3}>
              <DocumentUpload label="Miscellaneous" fileKey="miscellaneous" multiple />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={2} justifyContent="flex-end" className="order-documents-next-button">
        {aiState === 'error' && (
          <Grid size={12}>
            <Alert severity="error" variant="filled" onClose={() => setAiState('')}>
              There was an error connecting to the AI model. Please try again.
            </Alert>
          </Grid>
        )}
        <Grid>
          <Button
            variant="contained"
            onClick={handleReadAi}
            disabled={
              !order.PurchaseVehicle ||
              files.governmentaward.length === 0 ||
              (order.PurchaseVehicle !== 'ICPT UCOR' &&
                files.vendorquote.length === 0 &&
                supplierQuotes.length === 0) ||
              (order.Urgent && !order.UrgentReason) ||
              (order.RiskBuy && !riskBuyCompliant)
            }
            loading={aiState === 'loading'}
          >
            Next
          </Button>
        </Grid>
      </Grid>
      <Drawer
        className="header-crm-details-drawer"
        open={drawerOpen === 'opportunity'}
        onClose={() => setDrawerOpen(null)}
        anchor="right"
      >
        <HeaderCRMDetails crmOnly quoteId={quoteId} setDrawerState={() => setDrawerOpen(null)} />
      </Drawer>
    </>
  )
}

export default OrderDocuments
